import React, { useEffect, useState } from 'react';
import { getPlantingCyclesByCrop } from '../../../services/v2/plantingCycleService';
import { ProgressBar, Card, Badge } from 'react-bootstrap';
import LoadingComponent from '../../loading/LoadingComponent';

const CycleList = ({ cropId, onSelectCycle }) => {
    const [cycles, setCycles] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchCycles = async () => {
            try {
                const data = await getPlantingCyclesByCrop(cropId);
                setCycles(data || []);
            } catch (error) {
                console.error("Error fetching cycles", error);
            } finally {
                setLoading(false);
            }
        };

        fetchCycles();
    }, [cropId]);

    const closureDateLabel = (type) => {
        switch (type) {
            case 'HARVEST_END': return 'Fin cosecha';
            case 'HARVEST_PEAK': return 'Pico cosecha';
            case 'HARVEST_START': return 'Inicio cosecha';
            case 'ADMIN_CLOSURE': return 'Abandonado';
            default: return 'Cierre';
        }
    };

    const getStatusInfo = (status) => {
        switch (status) {
            case 'ACTIVE':
                return { label: 'Activo', variant: 'success', border: 'border-l-green-500' };
            case 'HARVESTING':
                return { label: 'Cosechando', variant: 'warning', border: 'border-l-orange-400' };
            case 'FINISHED':
                return { label: 'Finalizado', variant: 'secondary', border: 'border-l-gray-400' };
            case 'COMPLETED':
                // Cierre automático del sistema (fases completas, sin cosecha registrada),
                // distinto de FINISHED (cierre manual con cosecha) — variant/color propios
                // para no confundirlos.
                return { label: 'Completado', variant: 'info', border: 'border-l-blue-300' };
            case 'ABANDONED':
                return { label: 'Abandonado', variant: 'danger', border: 'border-l-red-400' };
            default:
                return { label: status || 'N/A', variant: 'secondary', border: 'border-l-gray-200' };
        }
    };

    if (loading) return <LoadingComponent />;

    if (cycles.length === 0) {
        return (
            <div className="text-center py-10 bg-white rounded-xl shadow-sm border border-gray-100">
                <span className="material-icons text-gray-300 text-5xl mb-3">history</span>
                <p className="text-gray-500">No hay ciclos de cultivo registrados para este terreno.</p>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cycles.map((cycle) => {
                const statusInfo = getStatusInfo(cycle.status);
                
                // Time calculation in Spanish
                const days = cycle.daysCount || 0;
                const weeks = Math.floor(days / 7);
                const remainingDays = days % 7;
                const timeLabel = weeks > 0 
                    ? `${weeks} sem ${remainingDays > 0 ? `${remainingDays} d` : ''}` 
                    : `${days} días`;

                return (
                    <Card 
                        key={cycle.id} 
                        onClick={() => onSelectCycle(cycle.id)}
                        className={`border-0 shadow-sm rounded-xl overflow-hidden hover:shadow-md transition-shadow cursor-pointer border-l-4 ${statusInfo.border}`}
                    >
                        <Card.Body className="p-5">
                            <div className="flex justify-between items-start mb-3">
                                <div>
                                    <h6 className="font-bold text-gray-800 mb-0">{cycle.name?.trim() ? cycle.name : `Ciclo #${cycle.id}`}</h6>
                                    <p className="text-xs text-gray-500">{cycle.varietyName || 'Variedad N/A'}</p>
                                </div>
                                <Badge bg={statusInfo.variant} className="rounded-full px-2 py-1">
                                    {statusInfo.label}
                                </Badge>
                            </div>

                            <div className="mb-4">
                                <div className="flex justify-between text-xs mb-1">
                                    <span className="text-gray-500">Progreso</span>
                                    <span className="font-bold text-gray-700">{cycle.progress || 0}%</span>
                                </div>
                                <ProgressBar now={cycle.progress || 0} variant={statusInfo.variant === 'secondary' || statusInfo.variant === 'danger' ? statusInfo.variant : 'success'} className="h-1.5 rounded-full" />
                            </div>

                            <div className={`grid gap-3 mb-4 ${cycle.closureReferenceDate ? 'grid-cols-3' : 'grid-cols-2'}`}>
                                <div className="bg-gray-50 p-2 rounded-lg text-center">
                                    <p className="text-[10px] text-gray-400 uppercase font-bold mb-0">Inicio</p>
                                    <p className="text-xs font-semibold text-gray-700">{cycle.startDate}</p>
                                </div>
                                {cycle.closureReferenceDate && (
                                    <div className="bg-gray-50 p-2 rounded-lg text-center">
                                        <p className="text-[10px] text-gray-400 uppercase font-bold mb-0">
                                            {closureDateLabel(cycle.closureReferenceDateType)}
                                        </p>
                                        <p className="text-xs font-semibold text-gray-700">{cycle.closureReferenceDate}</p>
                                    </div>
                                )}
                                <div className="bg-gray-50 p-2 rounded-lg text-center">
                                    <p className="text-[10px] text-gray-400 uppercase font-bold mb-0">Duración</p>
                                    <p className="text-xs font-semibold text-gray-700">{timeLabel}</p>
                                </div>
                            </div>

                            <div className="flex items-center justify-between mt-2 pt-3 border-t border-gray-50">
                                <div className="flex items-center">
                                    <span className="material-icons-outlined text-green-600 text-sm mr-1">spa</span>
                                    <span className="text-xs font-bold text-gray-600 truncate max-w-[120px]">{cycle.currentStage || 'N/A'}</span>
                                </div>
                                <button className="text-green-600 text-xs font-bold hover:underline">
                                    Ver Detalle
                                </button>
                            </div>
                        </Card.Body>
                    </Card>
                );
            })}
        </div>
    );
};

export default CycleList;
